import { useEffect, useState } from 'react';

import { useContainerBreakpoint } from 'src/hooks';
import { SoccerMatchControl } from 'src/types/matchControl';
import { CardTypes } from 'src/types/matchControl/card';
import { Clock, MainClock } from 'src/types/matchControl/clock';
import { Contestant, SoccerContestant } from 'src/types/matchControl/contestant';
import { Control } from 'src/types/matchControl/control';
import { ShootOut as ShootOutType } from 'src/types/matchControl/shootOut';

import { ShootOut } from '../../components';
import { LineUp } from '../../components/LineUp/LineUp';
import PenaltyClockSection from '../../components/PenaltyClockSection';
import styles from '../../MatchControl.module.scss';
import { MatchControlVariant } from '../../types';

import ClockRow from './ClockRow';
import ScoreRow from './ScoreRow';

const SHOOT_OUT_ID = 'shootOut';

type Props = {
  matchControl: SoccerMatchControl;
  matchControlGraphics: Control[];
  handleStartTime: (clock: MainClock) => void;
  handleStopTime: (clock: MainClock) => void;
  handleDispatchControl: (control: Control | undefined) => void;
  handleChangeTime: (clock: MainClock, time: number) => void;
  handleChangeExtraTime: (clock: MainClock, amount: number) => void;
  handleChangeCards: (contestant: SoccerContestant, card: CardTypes, amount: number) => void;
  handleChangeShortName: (contestant: SoccerContestant, name: string) => void;
  handleChangeScore: (contestant: SoccerContestant, amount: number) => void;
  handleChangeColor: (
    contestant: SoccerContestant,
    color: 'primaryColor' | 'secondaryColor',
    value: string
  ) => void;
  disabled?: boolean;
  variant?: MatchControlVariant;
  handleUpdateMatchControl: (matchControl: SoccerMatchControl) => void;
  handleUpdateShootOut: (shootOut: ShootOutType) => void;
  handleCreateClock: (type: Clock['type'], contestantId?: Contestant['id']) => void;
  handleDeleteClock: (id: Clock['id']) => void;
};

const Soccer = ({
  matchControl,
  handleChangeCards,
  handleChangeColor,
  handleChangeExtraTime,
  handleChangeScore,
  handleChangeShortName,
  handleChangeTime,
  handleDispatchControl,
  handleStartTime,
  handleStopTime,
  handleUpdateMatchControl,
  handleUpdateShootOut,
  matchControlGraphics,
  handleCreateClock,
  handleDeleteClock,
  variant = 'default',
}: Props) => {
  const { clocks, contestants, shootOut } = matchControl;
  const [mainClock] = clocks;
  const [home, away] = contestants;
  const [shootOutState, setShootOutState] = useState<ShootOutType | undefined>(shootOut);

  const { ref: wrapperRef, isBelowBreakpoint: isCompact } = useContainerBreakpoint();

  useEffect(() => {
    setShootOutState(shootOut);
  }, [shootOut]);

  const itemWrapperClass = isCompact ? styles.ItemWrapperCompact : styles.ItemWrapper;

  return (
    <div ref={wrapperRef} className={styles.MainWrapper}>
      <div className={itemWrapperClass}>
        <ClockRow
          mainClock={mainClock}
          matchControlGraphics={matchControlGraphics}
          isCompact={isCompact}
          handleStartTime={handleStartTime}
          handleStopTime={handleStopTime}
          handleDispatchControl={handleDispatchControl}
          handleChangeTime={handleChangeTime}
          handleChangeExtraTime={handleChangeExtraTime}
          onAdd={(amount) => handleChangeTime(mainClock, mainClock.clockTime + amount * 1000)}
          onSubtract={(amount) => handleChangeTime(mainClock, mainClock.clockTime - amount * 1000)}
        />
      </div>

      <div className={itemWrapperClass}>
        <ScoreRow
          home={home}
          away={away}
          isCompact={isCompact}
          variant={variant}
          handleChangeScore={handleChangeScore}
          handleChangeColor={handleChangeColor}
          handleChangeCards={handleChangeCards}
          handleChangeShortName={handleChangeShortName}
        />
      </div>

      {matchControl?.penaltyClocks?.enabled && (
        <>
          <div className={styles.Divider} />
          <PenaltyClockSection
            away={away}
            home={home}
            handleCreateClock={handleCreateClock}
            handleDeleteClock={handleDeleteClock}
          />
        </>
      )}

      <div className={styles.Divider} />

      <div>
        <ShootOut
          hasShootOut={matchControl.hasShootOut}
          handleToggleShootOut={() => {
            const updatedMatchControl = { ...matchControl, hasShootOut: !matchControl.hasShootOut };
            handleUpdateMatchControl(updatedMatchControl);
          }}
          isLive={matchControlGraphics?.some(
            (control) => control.controlId === SHOOT_OUT_ID && control.live
          )}
          handleClickLive={() =>
            handleDispatchControl(matchControlGraphics?.find((c) => c.controlId === SHOOT_OUT_ID))
          }
          shootOutTaker={shootOutState?.taker || 'home'}
          home={{ shortName: home.shortName, attempts: shootOutState?.home || [] }}
          away={{ shortName: away.shortName, attempts: shootOutState?.away || [] }}
          handleUpdateShootOut={(shootOut) => setShootOutState(shootOut)}
          handleClickUpdate={() => {
            if (!shootOutState) return;
            handleUpdateShootOut(shootOutState);
          }}
        />
      </div>

      <div className={styles.Divider} />

      <LineUp home={home} away={away} />
    </div>
  );
};

export default Soccer;
